import React, { Dispatch, ReactElement, SetStateAction, CSSProperties, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import Button from '../../../atoms/buttons/Button';
import RoundCheckIcon from '../../../atoms/icons/RoundCheckIcon';
import { PHONE_VERIFICATION_TYPES } from '../../../constants/phone';
import { IsMobileViewPort } from '../../../utils/helpers';
import { FormWrapper, Heading, Description, ButtonAction } from './styledElements';
import usePhoneActionModal from '../../../hooks/usePhoneActionModal';
import { handlePhoneSettingsVysionEvent } from '../../../utils/vysion/phoneSettings';
import { VYSION_ACTIONS } from '../../../utils/vysion';
import { PhoneSettingsViewVysionEventsEnum } from '../../../utils/vysion/phoneSettings/view';
import { PhoneSettingsClickVysionEventsEnum } from '../../../utils/vysion/phoneSettings/click';
import { DashlineSeparator, HCaptchaContainer } from '../../common/CommonStyle';
import { RootState } from '../../../store';
import HCaptcha from '../../auth/Hcaptcha';

interface IVerifyAccountOwnerShipProps {
  nextStep: () => void;
  handleCurrenStepVerification: Dispatch<SetStateAction<any>>;
  currentPhone: string;
  customStyle?: CSSProperties;
  onCancel?: () => void;
}

const VerifyAccountOwnerShip = ({
  nextStep,
  handleCurrenStepVerification,
  currentPhone,
  customStyle,
  onCancel,
}: IVerifyAccountOwnerShipProps): ReactElement => {
  const {
    utils: { requestResendOtp },
  } = usePhoneActionModal();
  const [isProcessing, setIsProcessing] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const { is_member_hcaptcha_enabled, cf_country } = useSelector((state: RootState) => state.settings);
  const handleSendCode = () => {
    setIsProcessing(true);

    handlePhoneSettingsVysionEvent({
      action: VYSION_ACTIONS.CLICK,
      event: PhoneSettingsClickVysionEventsEnum.CLICK_SEND_CODE,
    });

    requestResendOtp({
      isLogin: false,
      phoneNumber: currentPhone,
      showSuccessModal: false,
      onResendSuccess: () => {
        nextStep();
        handleCurrenStepVerification(PHONE_VERIFICATION_TYPES.OTP);
        handlePhoneSettingsVysionEvent({
          action: VYSION_ACTIONS.VIEW,
          event: PhoneSettingsViewVysionEventsEnum.VIEW_CURRENT_NUMBER_VERIFICATION,
        });
        setIsProcessing(true);
      },
      onResendFailed: onCancel,
      onResendCompleted: () => {
        //nothing to pass
      },
      hCaptcha: captcha,
    });
  };

  useEffect(() => {
    handlePhoneSettingsVysionEvent({
      action: VYSION_ACTIONS.VIEW,
      event: PhoneSettingsViewVysionEventsEnum.VIEW_ACCOUNT_MENU_VERIFY_ACCOUNT,
    });
  }, []);

  return (
    <FormWrapper style={customStyle}>
      {!IsMobileViewPort && <RoundCheckIcon />}
      <Heading>Verify Account Ownership</Heading>
      <Description>
        <strong>Send Code</strong> will send a verification code to the current phone number on file.
      </Description>
      {is_member_hcaptcha_enabled && (
        <>
          <DashlineSeparator />
          <HCaptchaContainer style={{ marginTop: 20 }}>
            <HCaptcha cfCountry={cf_country} storeCaptchaResponse={token => setCaptcha(token)} />
          </HCaptchaContainer>
        </>
      )}
      <DashlineSeparator style={{ margin: '20px 0 0' }} />

      <ButtonAction>
        <Button
          data-test-id="phone-settings-button"
          buttonType="primaryRed"
          onClick={handleSendCode}
          disabled={isProcessing || (is_member_hcaptcha_enabled && isEmpty(captcha))}
          type={'button'}
          size="small"
          customStyle={{ width: '100%' }}
        >
          {isProcessing ? 'Sending Code...' : 'Send Code'}
        </Button>
      </ButtonAction>
    </FormWrapper>
  );
};

export default VerifyAccountOwnerShip;
